import { useState, useEffect } from "react";
import { api, bookUrl } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageContext";
import { X, Search, BookOpen } from "lucide-react";
import { Input } from "./ui/input";

/**
 * Golden bookmark ribbon that hangs from the top of the book.
 * Click → opens a parchment-style TOC drawer with all 39 Tanakh books.
 * User selects a book → navigates to that book's chapter 1.
 *
 * Hebrew copy is preserved byte-identically via he.json key parity, so
 * Hebrew-locale rendering is regression-free; English copy is rendered
 * through `t()` from en.json.
 */
export default function BookNavRibbon({ currentBook }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.books().then(setData).catch(() => {});
  }, []);

  const sections = data?.sections || [];
  const ql = q.trim().toLowerCase();
  const matches = (b) =>
    !ql ||
    b.he.includes(q) ||
    b.en.toLowerCase().includes(ql) ||
    b.slug.toLowerCase().includes(ql);

  const onSelect = (slug) => {
    setOpen(false);
    navigate(`/reader/${bookUrl(slug)}/1`);
  };

  return (
    <>
      {/* Ribbon tab — hangs on the LEFT (binding side in our RTL book) */}
      <button
        data-testid="book-nav-ribbon"
        onClick={() => setOpen(true)}
        aria-label={t("nav_ribbon.tab_aria")}
        className="chrome fixed top-24 left-0 z-40 flex flex-col items-center gap-2 group"
      >
        <div className="h-48 w-8 bg-gradient-to-b from-[hsl(36,75%,38%)] via-[hsl(44,70%,50%)] to-[hsl(36,75%,34%)] rounded-e-sm shadow-lg relative overflow-hidden transition-all duration-300 group-hover:w-10 group-hover:shadow-xl">
          <span
            className="absolute inset-0 flex items-center justify-center font-heading text-[hsl(216,60%,15%)] font-bold"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            {t("nav_ribbon.tab_label")}
          </span>
          {/* Ribbon tail */}
          <span className="absolute -bottom-3 left-0 right-0 h-3 bg-[hsl(36,75%,34%)]"
                style={{ clipPath: "polygon(0 0, 100% 0, 50% 100%)" }} />
        </div>
      </button>

      {/* Drawer */}
      {open && (
        <div
          data-testid="book-nav-drawer"
          className="fixed inset-0 z-50 flex"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative h-full w-full max-w-md bg-background border-e border-gold/30 shadow-2xl overflow-y-auto p-6 animate-in slide-in-from-left duration-300"
            dir={isEn ? "ltr" : "rtl"}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="font-latin italic text-xs tracking-[0.3em] uppercase text-gold">
                  Table of Contents
                </p>
                <h2 className="font-heading text-3xl text-foreground">
                  {t("nav_ribbon.drawer_title")}
                </h2>
              </div>
              <button
                data-testid="close-nav"
                onClick={() => setOpen(false)}
                className="p-2 rounded-sm hover:bg-gold/10"
                aria-label={t("nav_ribbon.close_aria")}
              >
                <X className="h-5 w-5 text-foreground" strokeWidth={1.5} />
              </button>
            </div>

            <div className="ornamental-divider mb-6">✦</div>

            <div className="relative mb-6">
              <Search className="absolute top-1/2 -translate-y-1/2 end-3 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              <Input
                data-testid="nav-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("nav_ribbon.search_placeholder")}
                className={`bg-card border-border/60 ${isEn ? "pl-3 pr-10 text-left" : "pr-3 pl-10 text-right"} focus-visible:ring-gold`}
                dir={isEn ? "ltr" : "rtl"}
              />
            </div>

            {sections.map((section) => {
              const filtered = section.books.filter(matches);
              if (filtered.length === 0) return null;
              return (
                <div key={section.key} className="mb-8" data-testid={`nav-section-${section.key}`}>
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="font-heading text-xl text-gold">
                      {isEn ? section.en : section.he}
                    </h3>
                    <span className="font-latin italic text-xs text-muted-foreground">
                      {isEn ? section.he : section.en}
                    </span>
                  </div>
                  <div className="gold-rule mb-3" />
                  <div className="grid grid-cols-2 gap-2">
                    {filtered.map((b) => (
                      <button
                        key={b.slug}
                        data-testid={`nav-book-${b.slug.replace(/ /g, "-")}`}
                        onClick={() => onSelect(b.slug)}
                        className={`${isEn ? "text-left" : "text-right"} rounded-sm border p-3 transition-all ${
                          currentBook === b.slug
                            ? "border-gold bg-gold/10 text-foreground"
                            : "border-border/60 hover:border-gold/50 hover:bg-gold/5 text-foreground/85"
                        }`}
                      >
                        <div className="font-heading text-lg">{isEn ? b.en : b.he}</div>
                        <div className="font-latin italic text-xs text-muted-foreground mt-0.5">
                          {isEn ? b.he : b.en} · {b.chapters}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="pt-6 flex items-center justify-center">
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/library");
                }}
                className="sacred-btn-ghost"
                data-testid="nav-open-library"
              >
                <BookOpen className="h-4 w-4" strokeWidth={1.5} />
                {t("nav_ribbon.open_library")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
